import { Avatar, Box, Flex, Heading, Stack, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { faComments } from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import CourseQ from '@/queries/CourseQ'
import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useLocationHash from '@/client/hooks/useLocationHash'
import type { Query } from '@/client/query'
import { LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER } from '@/client/services/learnerFeedback/constants'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { getPositionInSeries, getSessionUrl } from '@/client/services/session'

import HostAvatarsFromQuery from '@/components/Host/HostAvatarsFromQuery'
import { toRegistrationState } from '@/components/RegistrationState/helpers'
import RegistrationState from '@/components/RegistrationState/RegistrationState'
import SessionDateTooltip from '@/components/Tooltips/SessionDateTooltip'
import IconWithText from '@/components/UI/IconWithText'
import LinkableDiv from '@/components/UI/LinkableDiv'
import LiveNowTag from '@/components/UI/LiveNowTag/LiveNowTag'
import MessageBox from '@/components/UI/MessageBox'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import RichText from '@/components/UI/RichTextEditor/RichText'
import SoftLink from '@/components/UI/SoftLink'
import Tippy from '@/components/UI/Tippy'

export function getSessionRowHash(sessionId: number): string {
  return `session-${sessionId}`
}

const SessionRowContent: React.FC<{
  session: Query
  entrypoint: boolean
  // ^ whether or not this session row is an entrypoint to a series/session, or purely visual
}> = ({ session, entrypoint }) => {
  const user = useUser()
  const [sessionIsActive, setSessionIsActive] = React.useState(false)

  const sessionId = session(SessionQ.id) ?? 0

  const sessionStartDateTime = DateTime.fromISO(`${session(SessionQ.start_date)}`)
  const sessionEndDateTime = DateTime.fromISO(`${session(SessionQ.getComputedEndDate())}`)
  const topic = session.one(SessionQ.getTopic())
  const topicName = topic(TopicQ.name)
  const course = topic.one(TopicQ.getCourse())
  const courseName = course(CourseQ.name)
  const discussionCount = session(SessionQ.countSessionComments()) ?? 0
  const sessionDate = `${session(SessionQ.start_date)}`
  const sessionDescription = session(SessionQ.description) ?? EMPTY_RICH_TEXT_VALUE
  const sessionRegistrations = session(SessionQ.countSessionRegistration()) ?? 0
  const sessionMaxLearners = session(SessionQ.max_learners) ?? 0
  const sessionType = session(SessionQ.series_id) === null ? 'session' : 'series session'
  const title = session(SessionQ.title)
  const attendancePolicy = session(SessionQ.attendance_policy)
  const isUserSelfReserved = session(SessionQ.hasUserSelfReservedSeries(user.id)) ?? false

  const inProgress =
    sessionStartDateTime < DateTime.local() && DateTime.local() < sessionEndDateTime
  const hasEnded = sessionEndDateTime < DateTime.local()
  const seriesSessionNumber = entrypoint ? null : getPositionInSeries(session)

  const sessionRegistration = session.one(
    SessionRegistrationQ.where({
      sessionId: SessionQ.id,
      userId: user.id,
    })
  )

  const isUserRegistered = sessionRegistration(SessionRegistrationQ.id) != null

  const learnerFeedbacks = session.many(
    LearnerFeedbackQ.where({
      type: LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER,
      sessionId: SessionQ.id,
      revieweeUserId: user.id,
      include: [
        {
          association: 'Reviewer',
          required: true,
        },
      ],
    })
  )
  const learnerFeedbackInfos = learnerFeedbacks.map(learnerFeedback => ({
    id: learnerFeedback(LearnerFeedbackQ.id) ?? 0,
    comment: learnerFeedback(LearnerFeedbackQ.comment) ?? '',
    reviewerDisplayName: learnerFeedback(UserQ.display_name),
  }))

  const locationHash = useLocationHash()

  React.useEffect(() => {
    setSessionIsActive(locationHash === getSessionRowHash(sessionId))
  }, [locationHash, sessionId])

  const messageBoxColorProps = sessionIsActive ? { bgColor: 'yellow.muted' } : {}

  return (
    <>
      <LinkableDiv id={getSessionRowHash(sessionId)} />
      <Box
        borderRadius="8px"
        width="100%"
        border={`2px solid ${chakraTokenToVar('border')}`}
        backgroundColor="bg"
        _hover={
          entrypoint
            ? {
                cursor: 'pointer',
                backgroundColor: 'bg.subtle',
                border: `2px solid ${chakraTokenToVar('primary.border')}`,
              }
            : {}
        }
        _active={
          entrypoint
            ? {
                cursor: 'pointer',
                backgroundColor: 'bg.muted',
                border: `2px solid ${chakraTokenToVar('border.emphasized')}`,
              }
            : {}
        }
      >
        <Box p="24px">
          <Flex mb={3} justifyContent="space-between">
            <Stack direction="row" align={['top', 'center']} flex={1} spacing={[0, '16px']}>
              <Avatar
                src={`${topic(TopicQ.icon)}`}
                name={`${topic(TopicQ.name)}`}
                width="80px"
                height="80px"
                borderRadius="md"
                display={['none', 'inline']}
              />
              <Box flex={1}>
                <Flex mb="8px" justifyContent="space-between" alignItems="center">
                  <Text variant="eyebrow">
                    {seriesSessionNumber !== null
                      ? `Session ${seriesSessionNumber}`
                      : `${courseName} · ${sessionType}`}
                  </Text>
                  {attendancePolicy === 'required' ? (
                    <Tag colorScheme="yellow">REQUIRED</Tag>
                  ) : null}
                </Flex>
                <Heading size="lg" as="h4" mb={{ base: '8px', sm: '4x' }}>
                  {title ?? topicName}
                </Heading>
                <Wrap alignItems="flex-start" spacing="8px">
                  {inProgress ? (
                    <WrapItem>
                      <LiveNowTag />
                    </WrapItem>
                  ) : null}
                  <WrapItem>
                    <SessionDateTooltip
                      sessionStartDateTime={sessionStartDateTime}
                      sessionEndDateTime={sessionEndDateTime}
                    >
                      <Text fontWeight="bold" color="fg.muted">
                        <RelativeTime time={sessionDate} includeWeekday />
                      </Text>
                    </SessionDateTooltip>
                  </WrapItem>
                </Wrap>
              </Box>
            </Stack>
          </Flex>
          <Box mt="16px" mb="24px" wordBreak="break-word">
            <RichText content={sessionDescription} />
          </Box>
        </Box>
        {entrypoint ? (
          <Flex direction="column" px="32px" pb="32px">
            <Flex justify="space-between" align="center">
              <Stack
                maxWidth="100%"
                overflow="hidden"
                direction="row"
                justifyContent="center"
                align="center"
                spacing={['8px', '16px', '24px']}
              >
                <HostAvatarsFromQuery sessionOrSeries={session} isSession isTooltip />
                <Stack
                  flex={1}
                  overflow="hidden"
                  maxWidth="100%"
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={['8px', '16px', '24px']}
                  display={['none', 'none', 'flex']}
                >
                  {seriesSessionNumber != null ? null : (
                    <Tippy content={pluralize(discussionCount, 'comment')}>
                      <Box>
                        <IconWithText faIcon={faComments} text={`${discussionCount}`} />
                      </Box>
                    </Tippy>
                  )}
                </Stack>
              </Stack>
              {seriesSessionNumber != null ? null : (
                <RegistrationState
                  state={toRegistrationState({ hasEnded, isUserRegistered, isUserSelfReserved })}
                  numRegistrations={sessionRegistrations}
                  maxLearners={sessionMaxLearners}
                  isSeries={false}
                />
              )}
            </Flex>
            {learnerFeedbackInfos.length > 0
              ? learnerFeedbackInfos.map(learnerFeedbackInfo => {
                  const messageText = `"${learnerFeedbackInfo.comment}" - ${learnerFeedbackInfo.reviewerDisplayName}`
                  return (
                    <Box key={learnerFeedbackInfo.id} mt={4}>
                      <MessageBox text={messageText} {...messageBoxColorProps} />
                    </Box>
                  )
                })
              : null}
          </Flex>
        ) : null}
      </Box>
    </>
  )
}

const SessionRow: React.FC<{
  session: Query
  entrypoint: boolean
}> = ({ session, entrypoint }) => {
  const user = useUser()
  const seriesId = session(SessionQ.series_id)
  const sessionId = session(SessionQ.id) ?? 0
  const isSessionHost = session(SessionQ.isHost({ userId: user.id })) ?? false
  const topic = session.one(SessionQ.getTopic())
  const topicName = `${topic(TopicQ.name)}`
  const href = getSessionUrl({
    sessionId,
    seriesId,
    topicName,

    isUserTutor: isSessionHost,
  })

  return entrypoint ? (
    <SoftLink href={href}>
      <SessionRowContent session={session} entrypoint />
    </SoftLink>
  ) : (
    <SessionRowContent session={session} entrypoint={false} />
  )
}

export default SessionRow
